<template>
  <v-dialog overlay-opacity="0.6" max-width="980" :value="value" @input="closeDialog">
    <v-container fill-height fluid grid-list-xl>
      <v-layout justify-center wrap>
        <v-flex md12>
          <base-material-card class="pa-0" color="blue">
            <template v-slot:heading>
              <v-row class="pa-0">
                <v-col cols="1" class="pa-0">
                  <v-icon large>mdi-message-text</v-icon>
                </v-col>
                <v-col align="center" cols="10" class="pa-0 card-title-text" style="font-size: 25px">
                  {{ $t("automazione-test-title") }}
                  <span v-if="assistant && assistant.name">- {{ assistant.name }}</span>
                </v-col>
                <v-col cols="1" class="pa-0">
                  <v-icon @click="closeDialog" large>mdi-close</v-icon>
                </v-col>
              </v-row>
            </template>

            <v-card-text>
              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('automazione-test-contact-first-name')"
                    v-model="contact.first_name"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('automazione-test-contact-last-name')"
                    v-model="contact.last_name"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <v-card outlined class="conversation-box">
                    <v-card-text class="conversation-body">
                      <div v-if="!messages.length" class="text--secondary">
                        {{ $t("automazione-test-empty") }}
                      </div>

                      <div
                        v-for="item in messages"
                        :key="item.id + '-' + item.timestamp"
                        class="conversation-row"
                        :class="item.role === 'assistant' ? 'assistant' : 'user'"
                      >
                        <div class="conversation-meta">
                          <div>
                            <strong>{{ item.role === "assistant" ? assistantName : "Guest" }}</strong>
                            <span class="ml-2">{{ formatTimestamp(item.timestamp) }}</span>
                          </div>
                          <div>
                            <v-btn
                              v-if="item.debug"
                              x-small
                              text
                              color="primary"
                              @click="openDebug(item)"
                            >
                              {{ $t("automazione-test-debug") }}
                            </v-btn>
                          </div>
                        </div>
                        <div class="conversation-message">{{ item.content }}</div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    outlined
                    dense
                    rows="4"
                    :label="$t('automazione-test-input')"
                    v-model="inputMessage"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" class="text-right">
                  <v-btn text @click="closeDialog">{{ $t("gbl-cancel") }}</v-btn>
                  <v-btn
                    color="primary"
                    class="mr-2"
                    :loading="loading"
                    :disabled="!canSendGuestMessage"
                    @click="sendGuestMessage"
                  >
                    {{ $t("automazione-test-send-guest") }}
                  </v-btn>
                  <v-btn
                    color="primary"
                    outlined
                    :loading="loading"
                    :disabled="!canAnswerChatbot"
                    @click="answerChatbot"
                  >
                    {{ $t("automazione-test-answer-chatbot") }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </base-material-card>
        </v-flex>
      </v-layout>
    </v-container>

    <v-dialog max-width="900" :value="debugDialog" @input="closeDebug">
      <v-card>
        <v-card-title class="headline">{{ $t("automazione-test-debug-title") }}</v-card-title>
        <v-card-text>
          <pre class="debug-json">{{ selectedDebug }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="primary" @click="closeDebug">{{ $t("gbl-close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import automazione from "../../apis/automazione";

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    assistant: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      loading: false,
      inputMessage: "",
      contact: {
        first_name: "",
        last_name: ""
      },
      conversation: {
        conversation_id: null,
        messages: []
      },
      debugDialog: false,
      selectedDebug: ""
    };
  },

  computed: {
    messages() {
      return this.conversation && this.conversation.messages
        ? this.conversation.messages
        : [];
    },

    assistantName() {
      return this.assistant && this.assistant.name ? this.assistant.name : "Assistant";
    },

    canSendGuestMessage() {
      return (
        this.assistant &&
        this.assistant.id &&
        !this.loading &&
        this.inputMessage &&
        this.inputMessage.trim().length
      );
    },

    canAnswerChatbot() {
      return (
        this.assistant &&
        this.assistant.id &&
        !this.loading
      );
    }
  },

  watch: {
    value(val) {
      if (val) {
        this.fetchConversation(true);
      }
    },

    assistant: {
      handler() {
        if (this.value) {
          this.fetchConversation(true);
        }
      },
      deep: true
    }
  },

  methods: {
    closeDialog() {
      this.$emit("input", false);
    },

    formatTimestamp(ts) {
      if (!ts) return "";
      return new Date(ts).toLocaleString();
    },

    async fetchConversation(resetSession = false) {
      if (!this.assistant || !this.assistant.id) {
        this.conversation = { conversation_id: null, messages: [] };
        return;
      }

      this.loading = true;
      try {
        const result = await automazione.fetchOperatorTestConversation(
          this.assistant.id,
          resetSession
        );
        this.conversation = result.conversation || {
          conversation_id: null,
          messages: []
        };
        this.contact = Object.assign(
          { first_name: "", last_name: "" },
          this.conversation.contact || {}
        );
      } finally {
        this.loading = false;
      }
    },

    async sendGuestMessage() {
      if (!this.canSendGuestMessage) return;

      this.loading = true;
      try {
        const result = await automazione.sendOperatorGuestMessage({
          assistant_id: this.assistant.id,
          message: this.inputMessage.trim(),
          contact: this.contact
        });
        this.conversation = result.conversation;
        this.contact = Object.assign(
          { first_name: "", last_name: "" },
          this.conversation.contact || {}
        );
        this.inputMessage = "";
      } finally {
        this.loading = false;
      }
    },

    async answerChatbot() {
      if (!this.canAnswerChatbot) return;

      this.loading = true;
      try {
        const result = await automazione.answerOperatorChatbot({
          assistant_id: this.assistant.id,
          contact: this.contact
        });
        this.conversation = result.conversation;
        this.contact = Object.assign(
          { first_name: "", last_name: "" },
          this.conversation.contact || {}
        );
        this.inputMessage = "";
      } finally {
        this.loading = false;
      }
    },

    openDebug(item) {
      this.selectedDebug = JSON.stringify(item.debug || {}, null, 2);
      this.debugDialog = true;
    },

    closeDebug() {
      this.debugDialog = false;
      this.selectedDebug = "";
    }
  }
};
</script>

<style scoped>
.conversation-box {
  min-height: 360px;
}

.conversation-body {
  max-height: 420px;
  overflow-y: auto;
}

.conversation-row {
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
}

.conversation-row.user {
  background: #eef4ff;
}

.conversation-row.assistant {
  background: #f2f7ef;
}

.conversation-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  margin-bottom: 4px;
}

.conversation-message {
  white-space: pre-wrap;
  word-break: break-word;
}

.debug-json {
  white-space: pre-wrap;
  word-break: break-word;
  background: #f7f7f9;
  border-radius: 6px;
  padding: 12px;
  max-height: 520px;
  overflow-y: auto;
}
</style>
